import { DeleteIcon } from '@/components/_common';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { Divider, Modal, notification } from 'antd';
import { WorkOrderItemData } from '..';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import {
  useDeleteWorkOrderItem,
  useWorkOrderItemBenefitsRemove,
  WORKORDER_HAS_REMOVED_ITENS_KEY,
  WORKORDER_ITEMS_QUERY_KEY,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { useState } from 'react';
import WorkOrderRemoveItemModal from '../../WorkOrderRemoveItemModal';
import { useDisclosure } from '@/hooks';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { isAppPortocar } from '@/index';

interface Props {
  workOrderItem: WorkOrderItemData;
}

export default function WorkOrderItemRemove({ workOrderItem }: Props) {
  const queryChanel = useQueryChanel();

  const [modalOpened, modalAction] = useDisclosure();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const isPortoBaterry =
    isAppPortocar &&
    (!!workorder?.isPortoBatteryWorkOrder || !!workorder?.portoAssistantCode);

  const isClosed = complement?.status === WorkOrderComplementStatus.Closed;
  const isCancelled =
    complement?.status === WorkOrderComplementStatus.Cancelled;

  const [deleteKitItems, setDeleteKitItems] = useState(false);

  const workOrderItemDelete = useDeleteWorkOrderItem(deleteKitItems);
  const itemBenefitRemove = useWorkOrderItemBenefitsRemove();

  const handleConfirmDelete = () => {
    if (workOrderItem.type === WorkOrderItemType.Kit) {
      Modal.confirm({
        title: 'Remover kit do orçamento',
        content: (
          <>
            <p>{`Deseja realmente remover o kit "${workOrderItem.description}" do orçamento?`}</p>
            <strong>Todos os itens relacionados ao kit serão removidos</strong>
          </>
        ),
        okText: 'Remover',
        okType: 'danger',
        onOk: async () => await removeKitItems(),
      });
    } else {
      modalAction.open();
    }
  };

  const removeKitItems = async () => {
    try {
      setDeleteKitItems(true);

      await new Promise((resolve) => setTimeout(resolve, 500));

      const results = await Promise.all(
        workOrderItem?.children?.map(async (child) => {
          if (complement?.id) {
            try {
              return await workOrderItemDelete.mutateAsync({
                complementId: complement.id,
                workOrderItemId: child.id,
                hasPecaAi: !!child.pecaAiProductId,
              });
            } catch {
              return null;
            }
          }
        }) || [],
      );

      if (results.some((result) => result === null)) {
        notification.warning({
          message: 'Remoção parcial',
          description:
            'Alguns itens do kit não foram removidos devido a erros.',
        });
      } else {
        notification.success({
          message: 'Kit removido com sucesso',
          description: 'Todos os itens do kit foram deletados com sucesso!',
        });
      }
    } finally {
      setDeleteKitItems(false);

      queryChanel({
        invalidateQueries: [[`${WORKORDER_ITEMS_QUERY_KEY}/verify`]],
        resetQueries: [
          [WORKORDER_ITEMS_QUERY_KEY],
          [WORKORDER_HAS_REMOVED_ITENS_KEY, { complementId: complement?.id }],
        ],
      });
    }
  };

  if (isClosed || isCancelled) return null;

  return (
    <>
      {deleteKitItems && (
        <ModalLoadingOverlay messages={['Removendo kit do orçamento...']} />
      )}

      <Divider type="vertical" />

      <DeleteIcon
        onClick={handleConfirmDelete}
        disabled={
          workOrderItem.disableDelete ||
          !hasPermissions([Authorities.MOVIMENTATION_WORKORDER_DELETE_ITEM]) ||
          isPortoBaterry
        }
      />

      <WorkOrderRemoveItemModal
        open={modalOpened}
        item={workOrderItem}
        onCancel={modalAction.close}
        onCallback={async (params) => {
          if (!!workOrderItem.benefitServiceCode && complement) {
            return itemBenefitRemove.mutateAsync({
              itemId: workOrderItem.id,
              complementId: complement.id,
              items: [workOrderItem, ...(workOrderItem.children ?? [])],
            });
          }

          return workOrderItemDelete.mutateAsync(params).then(() => {
            queryChanel({
              invalidateQueries: [[`${WORKORDER_ITEMS_QUERY_KEY}/verify`]],
              resetQueries: [
                [WORKORDER_ITEMS_QUERY_KEY],
                [
                  WORKORDER_HAS_REMOVED_ITENS_KEY,
                  { complementId: complement?.id },
                ],
              ],
            });
          });
        }}
        confirmLoading={
          workOrderItemDelete.isLoading || itemBenefitRemove.isLoading
        }
      />
    </>
  );
}
